import { useEffect, useState } from "react";

import { CONFIG } from "@/config-global";
import type { MarketingPageVariantConfig } from "@/lib/marketing-page-renderer";
import type { MarketingPageKey } from "@/services/marketing-analytics";

const VISITOR_STORAGE_KEY = "imarketer.visitorId";

export type MarketingExperimentStatus = "running" | "draft" | "paused";

export type MarketingExperimentVariant = {
  id: string;
  name: string;
  weight: number;
  config?: MarketingPageVariantConfig;
};

export type MarketingExperiment = {
  id: string;
  pageKey: MarketingPageKey;
  status: MarketingExperimentStatus;
  variants: MarketingExperimentVariant[];
};

type ActiveExperimentResponse = {
  ok: boolean;
  data?: {
    experimentId: string;
    pageKey: MarketingPageKey;
    variants: MarketingExperimentVariant[];
  } | null;
};

export type MarketingVariantAssignment = {
  pageKey: MarketingPageKey;
  experimentId?: string;
  variantId: string;
  variantConfig?: MarketingPageVariantConfig;
};

const marketingExperiments: MarketingExperiment[] = [
  {
    id: "home-pricing-proof-v1",
    pageKey: "home",
    status: "paused",
    variants: [
      {
        id: "control",
        name: "Control",
        weight: 50,
      },
      {
        id: "pricing-above-proof",
        name: "Pricing above proof",
        weight: 50,
        config: {
          sectionOrder: [
            "hero",
            "logo-cloud",
            "pricing",
            "features",
            "video-showcase",
            "course-builder",
            "product-types",
            "calendar-integration",
            "community",
            "dashboard",
            "testimonials",
            "faq",
            "final-cta",
          ],
          ctaCopy: {
            "hero-primary": "Start Selling Free",
            "pricing-primary": "Start Selling for Free",
          },
        },
      },
    ],
  },
];

function createVisitorId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getMarketingVisitorId() {
  if (typeof window === "undefined") return "server";

  try {
    const storedVisitorId = window.localStorage.getItem(VISITOR_STORAGE_KEY);
    if (storedVisitorId) return storedVisitorId;

    const visitorId = createVisitorId();
    window.localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);

    return visitorId;
  } catch {
    return "anonymous";
  }
}

function hashString(value: string) {
  return Array.from(value).reduce((hash, character) => {
    const nextHash = (hash << 5) - hash + character.charCodeAt(0);
    return nextHash | 0;
  }, 0);
}

export function getActiveMarketingExperiment(pageKey: MarketingPageKey) {
  return marketingExperiments.find((experiment) => experiment.pageKey === pageKey && experiment.status === "running") || null;
}

export function getMarketingVariantConfig(pageKey: MarketingPageKey, variantId: string) {
  const experiment = marketingExperiments.find((candidate) => candidate.pageKey === pageKey);
  return experiment?.variants.find((variant) => variant.id === variantId)?.config || null;
}

export function assignMarketingVariant(pageKey: MarketingPageKey, experiment: MarketingExperiment | null): MarketingVariantAssignment {
  if (!experiment) {
    return { pageKey, variantId: "control" };
  }

  const weightedVariants = experiment.variants.filter((variant) => variant.weight > 0);
  if (!weightedVariants.length) {
    return { pageKey, experimentId: experiment.id, variantId: "control" };
  }

  const totalWeight = weightedVariants.reduce((total, variant) => total + variant.weight, 0);
  const visitorId = getMarketingVisitorId();
  const bucket = Math.abs(hashString(`${visitorId}:${experiment.id}`)) % totalWeight;
  let cursor = 0;
  const assignedVariant = weightedVariants.find((variant) => {
    cursor += variant.weight;
    return bucket < cursor;
  }) || weightedVariants[0];

  return {
    pageKey,
    experimentId: experiment.id,
    variantId: assignedVariant.id,
    variantConfig: assignedVariant.config,
  };
}

export function getMarketingVariantAssignment(pageKey: MarketingPageKey) {
  const activeExperiment = getActiveMarketingExperiment(pageKey);
  return assignMarketingVariant(pageKey, activeExperiment);
}

async function getRemoteActiveMarketingExperiment(pageKey: MarketingPageKey, siteId = "lint-demo") {
  const functionUrl = (CONFIG.marketingAnalytics.apiUrl || CONFIG.marketingAnalytics.functionUrl).trim();
  if (!functionUrl) return null;

  const queryParams = new URLSearchParams({
    endpoint: "experiments:active",
    page_key: pageKey,
    site_id: siteId,
  });

  const response = await fetch(`${functionUrl}?${queryParams.toString()}`);
  if (!response.ok) return null;

  const payload = await response.json() as ActiveExperimentResponse;
  const activeExperiment = payload.data;
  if (!payload.ok || !activeExperiment || !Array.isArray(activeExperiment.variants)) return null;

  return {
    id: activeExperiment.experimentId,
    pageKey: activeExperiment.pageKey,
    status: "running" as const,
    variants: activeExperiment.variants,
  } satisfies MarketingExperiment;
}

export function useMarketingVariantAssignment(pageKey: MarketingPageKey, siteId = "lint-demo") {
  const [assignment, setAssignment] = useState(() => getMarketingVariantAssignment(pageKey));

  useEffect(() => {
    let cancelled = false;
    setAssignment(getMarketingVariantAssignment(pageKey));

    // Public pages read the active experiment contract only; admin mutations stay behind authenticated Worker endpoints.
    void getRemoteActiveMarketingExperiment(pageKey, siteId).then((experiment) => {
      if (!cancelled && experiment) {
        setAssignment(assignMarketingVariant(pageKey, experiment));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pageKey, siteId]);

  return assignment;
}

export function getMarketingExperimentRows() {
  return marketingExperiments.map((experiment) => ({
    ...experiment,
    trafficSplit: experiment.variants.map((variant) => `${variant.name} ${variant.weight}%`).join(" / "),
  }));
}
